import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { History, Loader2 } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { useUserSelections } from '@/hooks/useUserSelections';
import { useCurrentMatchday } from '@/hooks/useCurrentMatchday';
import { useLeagueConfig } from '@/hooks/useLeagueConfig';
import { useSeason } from '@/context/SeasonContext';

export const MatchHistory = () => {
  const { user, currentMembership } = useAuth();
  const { season } = useSeason();
  const { competitionId } = useLeagueConfig();
  const leagueId = currentMembership?.league_id;
  const { data: currentMatchday } = useCurrentMatchday(season, competitionId);
  const { selectionData, isLoading } = useUserSelections(user?.id, leagueId, season);

  if (!user) {
    return (
      <Card>
        <CardContent className="p-6 text-center text-muted-foreground">
          Please log in to view your match history.
        </CardContent>
      </Card>
    );
  }

  // Helper to check if a selection's matchday has been completed
  const isMatchdayCompleted = (matchday: number) => {
    return matchday < (currentMatchday || 0);
  };

  // Calculate stats from selections
  // A selection is "completed" if it has been processed (either has match_result or processed=true)
  const completedSelections = selectionData.selections.filter(
    s => s.match_result !== null || s.processed
  );

  // Count wins/draws/losses using the actual match_result field
  // Legacy selections (processed=true but match_result=null) won't be counted in W/D/L
  const wins = completedSelections.filter(s => s.match_result === 'win').length;
  const draws = completedSelections.filter(s => s.match_result === 'draw').length;
  const losses = completedSelections.filter(s => s.match_result === 'loss').length;
  // Matchdays played = all completed selections (including legacy without match_result)
  const matchdaysPlayed = completedSelections.length;

  // Determine result badge based on match_result field
  // For legacy selections (processed before match_result column was added),
  // we show "Played" instead of "Processing" since they were already processed
  const getResultBadge = (
    matchResult: 'win' | 'draw' | 'loss' | null,
    matchday: number,
    processed: boolean
  ) => {
    if (matchResult === 'win') {
      return <Badge className="bg-green-100 text-green-800">Win</Badge>;
    } else if (matchResult === 'draw') {
      return <Badge className="bg-yellow-100 text-yellow-800">Draw</Badge>;
    } else if (matchResult === 'loss') {
      return <Badge className="bg-red-100 text-red-800">Loss</Badge>;
    } else if (processed) {
      // Legacy selection: processed before match_result column existed
      // Show "Played" since we know it was processed but don't have the result
      return <Badge className="bg-stone-200 text-stone-700">Played</Badge>;
    } else if (isMatchdayCompleted(matchday)) {
      // Matchday completed but selection not yet processed
      return <Badge className="bg-orange-100 text-orange-800">Processing</Badge>;
    }
    // Current/future matchday with no result yet
    return <Badge className="bg-stone-100 text-stone-800">Pending</Badge>;
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <History className="h-5 w-5" />
            Match History
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-stone-400" />
            </div>
          ) : selectionData.selections.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">
              No match history yet. Make your first pick!
            </p>
          ) : (
            <div className="space-y-4">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left p-3 text-sm font-medium text-stone-600">Matchday</th>
                      <th className="text-left p-3 text-sm font-medium text-stone-600">Team Selected</th>
                      <th className="text-center p-3 text-sm font-medium text-stone-600">Result</th>
                      <th className="text-center p-3 text-sm font-medium text-stone-600">Points</th>
                      <th className="text-center p-3 text-sm font-medium text-stone-600">TDD</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[...selectionData.selections]
                      .sort((a, b) => b.matchday - a.matchday)
                      .map((selection) => (
                        <tr key={selection.id} className="border-b hover:bg-stone-50">
                          <td className="p-3 font-medium">{selection.matchday}</td>
                          <td className="p-3">{selection.team_name}</td>
                          <td className="p-3 text-center">
                            {getResultBadge(selection.match_result, selection.matchday, selection.processed)}
                          </td>
                          <td className="p-3 text-center">
                            <span
                              className={`font-bold ${
                                selection.points_earned > 0
                                  ? 'text-green-600'
                                  : selection.points_earned < 0
                                  ? 'text-red-600'
                                  : 'text-stone-500'
                              }`}
                            >
                              {selection.points_earned === 0 ? '-' : selection.points_earned}
                            </span>
                          </td>
                          <td className="p-3 text-center">
                            {selection.used_double_down ? (
                              <Badge variant="secondary" className="text-xs">Yes</Badge>
                            ) : (
                              <span className="text-stone-400">-</span>
                            )}
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>

              {/* Summary Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6 pt-6 border-t">
                <div className="text-center p-4 bg-blue-50 rounded-lg">
                  <p className="text-2xl font-bold text-blue-600">{matchdaysPlayed}</p>
                  <p className="text-sm text-stone-600">Matchdays Played</p>
                </div>
                <div className="text-center p-4 bg-green-50 rounded-lg">
                  <p className="text-2xl font-bold text-green-600">{wins}</p>
                  <p className="text-sm text-stone-600">Wins</p>
                </div>
                <div className="text-center p-4 bg-yellow-50 rounded-lg">
                  <p className="text-2xl font-bold text-yellow-600">{draws}</p>
                  <p className="text-sm text-stone-600">Draws</p>
                </div>
                <div className="text-center p-4 bg-red-50 rounded-lg">
                  <p className="text-2xl font-bold text-red-600">{losses}</p>
                  <p className="text-sm text-stone-600">Losses</p>
                </div>
              </div>

              {/* TDD Usage */}
              <div className="mt-4 p-4 bg-stone-50 rounded-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-stone-700">Triple Down Days Used</p>
                    <p className="text-sm text-stone-500">
                      {selectionData.totalTddUsed} of 4 used ({selectionData.tddRemaining} remaining)
                    </p>
                  </div>
                  <div className="flex gap-1">
                    {[1, 2, 3, 4].map((i) => (
                      <div
                        key={i}
                        className={`w-4 h-4 rounded-full ${
                          i <= selectionData.totalTddUsed
                            ? 'bg-stone-700'
                            : 'bg-stone-200'
                        }`}
                      />
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

